"use client";

import { FC, RefObject, useContext, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { IExchangeRatePair, IWalletResponse } from "@finbackoffice/clientbff-client";
import { Currency, WalletType, isCryptoCurrency, AssetsDirectory } from "@finbackoffice/enums";
import { CurrencyShortNames } from "@finbackoffice/fe-core";
import { AuthContext } from "@finbackoffice/site-core";
import {
    ConfigContextV2,
    ExchangeRatesContextV2,
    WalletsContext,
    BetSlipActionsContext,
} from "contexts";
import { SkinVersions } from "utils/constants";
import Button from "components/base/button/Button";
import Loading from "components/base/loading/Loading";
import Translate from "components/base/translate/Translate";
import { CurrencyFormatter } from "components/base/currency-formater/CurrencyFormater";
import { Chars } from "utils/strings";
import { Svg } from "components/base/svg/Svg";
import FadeInAnimation from "components/base/fade-in/FadeInAnimation";
import { useDevice } from "hooks";
import useSkinVersion from "hooks/base/useSkinVersion";
import styles from "./wallet-selector.module.sass";

type IProps = {
    rates: IExchangeRatePair[];
    wallet: Partial<IWalletResponse>;
    fxCurrency: Currency | "";
    currentWallet: Partial<IWalletResponse>;
    token: string;
};

const WalletItem: FC<IProps> = ({ wallet, rates, fxCurrency, currentWallet, token }) => {
    const { setSelectedWallet } = useContext(WalletsContext);
    const { currency, balance, type } = wallet;
    const { siteDefaultCurrency } = useContext(ConfigContextV2);
    const { setBetError } = useContext(BetSlipActionsContext);
    const isDefaultCurrencyBalance =
        currency === siteDefaultCurrency && type === WalletType.Balance;

    const rate = useMemo(() => {
        const result = parseFloat(rates.find((pair) => pair.toCurrency === fxCurrency)?.rate ?? "");
        return !Number.isNaN(result) ? result : 0;
    }, [rates, fxCurrency]);

    const handleWalletChange = () => {
        setSelectedWallet(wallet);

        setBetError(null);
    };

    if (!currency) {
        return null;
    }

    return (
        <li
            data-testid={`currencies-dropdown-choice-${currency}`}
            className={classNames(
                currency === currentWallet.currency &&
                    type === currentWallet.type &&
                    styles.selected,
                currentWallet.type === WalletType.Bonus && styles.bonus,
            )}
            onClick={handleWalletChange}>
            <Svg
                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Currencies}/${currency.toLowerCase()}.svg`}
                className={classNames(
                    "svg-icon crypto-currency",
                    type === WalletType.Bonus && styles.typeBonus,
                )}
            />
            <div>
                <span>
                    {CurrencyShortNames[currency] || currency}
                    {balance && (
                        <CurrencyFormatter currency={currency} amount={balance} withCode={false} />
                    )}
                </span>
                <div className={styles.currency}>
                    {type === WalletType.Bonus && (
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/bonus-label.svg`}
                            className={styles.bonusLabel}
                        />
                    )}
                    {!isDefaultCurrencyBalance &&
                        (isCryptoCurrency(currency) || type === WalletType.Bonus) && (
                            <span className={`${type === WalletType.Bonus && styles.bonusWallet}`}>
                                <Translate
                                    tid={`currencies_${
                                        type === WalletType.Bonus ? "bonus" : currency.toLowerCase()
                                    }`}
                                />
                            </span>
                        )}
                    {!isDefaultCurrencyBalance && isCryptoCurrency(currency) && (
                        <span>
                            {token ? (
                                currency !== fxCurrency ? (
                                    rate ? (
                                        <>
                                            <i />
                                            {rate && balance && (
                                                <CurrencyFormatter
                                                    currency={fxCurrency}
                                                    amount={(parseFloat(balance) * rate).toString()}
                                                    withCode={false}
                                                    withSymbol
                                                    configOverride={
                                                        [
                                                            Currency.USDC_ERC20,
                                                            Currency.USDT_ERC20,
                                                            Currency.USDT_TRC20,
                                                        ].includes(fxCurrency as Currency)
                                                            ? { symbol: "$" }
                                                            : undefined
                                                    }
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <Loading />
                                    )
                                ) : null
                            ) : (
                                <CurrencyFormatter
                                    currency={Currency.USD}
                                    amount="0"
                                    withCode={false}
                                    withSymbol
                                />
                            )}
                        </span>
                    )}
                </div>
            </div>
        </li>
    );
};

const WalletSelector: FC = () => {
    const { siteConfigReady } = useContext(ConfigContextV2);
    const { exchangeRates, exchangeRate } = useContext(ExchangeRatesContextV2);
    const { currentWallet, currentBalance, siteSupportedWallets, currencyOfExchange } =
        useContext(WalletsContext);
    const { userToken } = useContext(AuthContext);
    const { isBetmidas, skinVersion } = useSkinVersion();

    const { isMobileDevice } = useDevice();

    const [showBalance, setShowBalance] = useState(true);
    const [openDropdown, setOpenDropdown] = useState(false);

    const dropdownRef: RefObject<HTMLDivElement | null> = useRef(null);

    const hideSelector =
        !userToken &&
        (isBetmidas ||
            skinVersion === SkinVersions.Betvivocomz ||
            skinVersion === SkinVersions.Bettorian);

    useEffect(() => {
        const outsideClickHandler = (event: any) => {
            if (dropdownRef && !dropdownRef.current?.contains(event.target)) {
                setOpenDropdown(false);
            }
        };

        if (openDropdown) {
            document.addEventListener("mousedown", outsideClickHandler);
        } else {
            document.removeEventListener("mousedown", outsideClickHandler);
        }

        return () => {
            document.removeEventListener("mousedown", outsideClickHandler);
        };
    }, [openDropdown]);

    return (
        <>
            {!hideSelector && (
                <div
                    className={styles.customDropdown}
                    ref={dropdownRef}
                    data-testid="currencies-dropdown-toggle"
                    onClick={() => currentWallet?.currency && setOpenDropdown(!openDropdown)}>
                    <div className={classNames(styles.balance, openDropdown && styles.opened)}>
                        {!isMobileDevice && (
                            <span
                                className={classNames("viewPass", showBalance ? "showPass" : "")}
                                onClick={() => setShowBalance(!showBalance)}
                            />
                        )}

                        {showBalance ? (
                            <Button type="button" variant="custom">
                                {siteConfigReady && !!currentWallet?.currency ? (
                                    <>
                                        {currentWallet.type === WalletType.Bonus && (
                                            <Svg
                                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/bonus-label.svg`}
                                                className={styles.bonusLabel}
                                            />
                                        )}
                                        <Svg
                                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Currencies}/${currentWallet?.currency?.toLowerCase()}.svg`}
                                            className={classNames(
                                                "svg-icon crypto-currency",
                                                currentWallet.type === WalletType.Bonus &&
                                                    styles.typeBonus,
                                            )}
                                        />
                                        <div>
                                            <CurrencyFormatter
                                                currency={currentWallet?.currency}
                                                amount={currentBalance}
                                                withCode={false}
                                            />
                                            {isCryptoCurrency(currentWallet?.currency) &&
                                                (userToken ? (
                                                    currentWallet?.currency !==
                                                    currencyOfExchange ? (
                                                        exchangeRate ? (
                                                            <span>
                                                                ≈{Chars.nbsp}
                                                                {exchangeRate && currentBalance && (
                                                                    <CurrencyFormatter
                                                                        currency={
                                                                            currencyOfExchange
                                                                        }
                                                                        amount={(
                                                                            parseFloat(
                                                                                currentBalance,
                                                                            ) * exchangeRate
                                                                        ).toString()}
                                                                        withCode={false}
                                                                        withSymbol
                                                                        configOverride={
                                                                            [
                                                                                Currency.USDC_ERC20,
                                                                                Currency.USDT_ERC20,
                                                                                Currency.USDT_TRC20,
                                                                            ].includes(
                                                                                currencyOfExchange as Currency,
                                                                            )
                                                                                ? {
                                                                                      symbol: "$",
                                                                                  }
                                                                                : undefined
                                                                        }
                                                                    />
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <Loading
                                                                wrapperClassName={styles.loader}
                                                            />
                                                        )
                                                    ) : null
                                                ) : (
                                                    <div>
                                                        <CurrencyFormatter
                                                            currency={Currency.USD}
                                                            amount="0"
                                                            withCode={false}
                                                            withSymbol
                                                        />
                                                    </div>
                                                ))}
                                        </div>
                                    </>
                                ) : (
                                    <Loading wrapperClassName={styles.loader} />
                                )}
                            </Button>
                        ) : (
                            <span onClick={() => setShowBalance(!showBalance)}>
                                <Translate tid="userPanel_showBalance" />
                            </span>
                        )}
                    </div>
                    {currentWallet && openDropdown && (
                        <FadeInAnimation>
                            <ul className={styles.dropdownList}>
                                {siteSupportedWallets.map((wallet: Partial<IWalletResponse>) => (
                                    <WalletItem
                                        key={`${wallet.id}-${wallet.version}`}
                                        rates={exchangeRates?.[wallet.currency as Currency] || []}
                                        wallet={wallet}
                                        fxCurrency={currencyOfExchange}
                                        currentWallet={currentWallet}
                                        token={userToken || ""}
                                    />
                                ))}
                            </ul>
                        </FadeInAnimation>
                    )}
                </div>
            )}
        </>
    );
};

export default WalletSelector;
